import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Calendar, Save, Clock } from "lucide-react";

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const ALL_CLUBS = ["Analistas", "Diplomatas", "Sentinelas", "Exploradores"];

/**
 * Allows admin/coach to set the weekly club meeting schedule.
 *
 * Props:
 *  - club: string | null. If null/empty (admin who has no club assigned),
 *    an internal selector lets the user pick any of the 4 clubs.
 *  - weeksTotal: number
 *  - currentWeek: number
 *  - mode: "admin" | "coach". When "coach", the default weekday/time is read-only
 *    (only admin can change the master schedule). Coach can still add per-week
 *    exceptions for holidays etc.
 */
export default function ClubMeetingScheduler({ club, weeksTotal = 10, currentWeek = 1, mode = "admin" }) {
  const [data, setData] = useState(null);
  const [selectedClub, setSelectedClub] = useState(club || ALL_CLUBS[0]);
  const [weekday, setWeekday] = useState(1);
  const [time, setTime] = useState("09:30");
  const [override, setOverride] = useState(""); // datetime-local string for current week
  const [saving, setSaving] = useState(false);

  // If parent passes a club, lock to it. Otherwise allow admin selection.
  useEffect(() => { if (club) setSelectedClub(club); }, [club]);
  const activeClub = club || selectedClub;

  const load = () =>
    api.get("/settings/club-meetings").then((r) => {
      setData(r.data);
      const c = r.data?.[activeClub];
      if (c) {
        if (c.weekday !== undefined && c.weekday !== null) setWeekday(c.weekday);
        if (c.time) setTime(c.time);
        const ov = c.overrides?.[currentWeek];
        setOverride(ov ? toLocalInput(ov) : "");
      } else {
        setWeekday(1); setTime("09:30"); setOverride("");
      }
    });

  useEffect(() => { if (activeClub) load(); }, [activeClub, currentWeek]);

  const saveDefault = async () => {
    setSaving(true);
    try {
      await api.put("/settings/club-meeting", { club: activeClub, weekday: Number(weekday), time });
      toast.success(`Reunião do ${activeClub}: ${WEEKDAYS[weekday]} ${time}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const saveOverride = async () => {
    if (!override) {
      try {
        await api.delete(`/settings/club-meeting/override?club=${encodeURIComponent(activeClub)}&week=${currentWeek}`);
        toast.success(`Exceção removida (semana ${currentWeek})`);
        load();
      } catch (err) { toast.error(formatApiError(err)); }
      return;
    }
    setSaving(true);
    try {
      const iso = new Date(override).toISOString();
      await api.put("/settings/club-meeting/override", { club: activeClub, week_number: currentWeek, iso_datetime: iso });
      toast.success(`Exceção semana ${currentWeek} guardada`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const cfg = data?.[activeClub] || {};
  const upcomingDt = (cfg.schedule || {})[currentWeek];

  const adminOverview = useMemo(() => {
    if (club || !data) return null;
    return ALL_CLUBS.map((c) => ({
      club: c,
      iso: data?.[c]?.schedule?.[currentWeek] || null,
      weekday: data?.[c]?.weekday,
      time: data?.[c]?.time,
    }));
  }, [data, currentWeek, club]);

  return (
    <div className="kw-card p-5" data-testid="club-meeting-scheduler">
      <SchedulerHeader activeClub={activeClub} club={club} selectedClub={selectedClub} setSelectedClub={setSelectedClub} />

      <div className="grid md:grid-cols-2 gap-5">
        <DefaultScheduleForm mode={mode} weekday={weekday} setWeekday={setWeekday}
          time={time} setTime={setTime} saving={saving} onSave={saveDefault} />
        <WeekOverrideForm currentWeek={currentWeek} mode={mode} override={override}
          setOverride={setOverride} saving={saving} onSave={saveOverride} />
      </div>

      {upcomingDt && <NextMeetingLine currentWeek={currentWeek} schedule={cfg.schedule || {}} />}
      {adminOverview && <AdminOverviewGrid currentWeek={currentWeek} rows={adminOverview} />}
    </div>
  );
}

function SchedulerHeader({ activeClub, club, selectedClub, setSelectedClub }) {
  return (
    <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3 flex-wrap">
      <Calendar className="w-5 h-5 text-[#E50000]" />
      <div className="flex-1 min-w-0">
        <div className="kw-overline">Reunião semanal · {activeClub}</div>
        <div className="text-xs text-[#737373]">Define dia e hora fixos da reunião. Lembretes automáticos (24h + 2h antes) serão enviados nesta hora.</div>
      </div>
      {!club && (
        <select className="kw-input !py-2 md:w-56" value={selectedClub}
          onChange={(e) => setSelectedClub(e.target.value)} data-testid="meeting-club-select">
          {ALL_CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
    </div>
  );
}

function DefaultScheduleForm({ mode, weekday, setWeekday, time, setTime, saving, onSave }) {
  return (
    <div>
      <div className="kw-overline mb-2">
        Por defeito (todas as semanas)
        {mode === "coach" && <span className="ml-2 text-[#737373] normal-case tracking-normal text-[10px]">· definido pelo admin</span>}
      </div>
      {mode === "coach" ? (
        <div className="flex gap-3 items-center border border-[#222] bg-[#0A0A0A] p-3" data-testid="meeting-default-readonly">
          <Clock className="w-4 h-4 text-[#E50000]" />
          <span className="font-display text-lg font-bold">{WEEKDAYS[weekday]} · {time}</span>
          <span className="text-[10px] text-[#737373] uppercase tracking-widest ml-auto">Read-only</span>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">Dia da semana</label>
            <select className="kw-input !py-2" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} data-testid="meeting-weekday">
              {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">Hora (Lisboa)</label>
            <input type="time" className="kw-input !py-2 !w-32" value={time}
              onChange={(e) => setTime(e.target.value)} data-testid="meeting-time" />
          </div>
          <button onClick={onSave} disabled={saving}
            className="kw-btn-primary inline-flex items-center gap-2" data-testid="meeting-save-default">
            <Save className="w-4 h-4" /> Guardar
          </button>
        </div>
      )}
    </div>
  );
}

function WeekOverrideForm({ currentWeek, mode, override, setOverride, saving, onSave }) {
  return (
    <div>
      <div className="kw-overline mb-2">
        Exceção · Semana {currentWeek}
        {mode === "coach" && <span className="ml-2 text-[#E50000] normal-case tracking-normal text-[10px]">· apenas para feriados / casos excepcionais</span>}
      </div>
      <div className="flex gap-2 flex-wrap items-end">
        <input type="datetime-local" className="kw-input !py-2"
          value={override} onChange={(e) => setOverride(e.target.value)}
          data-testid="meeting-override-input" />
        <button onClick={onSave} disabled={saving}
          className="kw-btn-secondary inline-flex items-center gap-2" data-testid="meeting-save-override">
          <Save className="w-4 h-4" /> {override ? "Guardar exceção" : "Remover exceção"}
        </button>
      </div>
      <div className="text-[10px] text-[#737373] mt-2">Deixa vazio para usar o horário por defeito.</div>
    </div>
  );
}

function NextMeetingLine({ currentWeek, schedule }) {
  const endOf = (iso) => new Date(iso).getTime() + 90 * 60000;
  const cur = schedule[currentWeek];
  const curDone = cur && endOf(cur) < Date.now();
  const nextWeek = Object.keys(schedule).map(Number).sort((a, b) => a - b)
    .find((w) => w >= currentWeek && schedule[w] && endOf(schedule[w]) >= Date.now());
  return (
    <div className="mt-4 pt-4 border-t border-[#222] space-y-1 text-sm">
      {curDone && (
        <div className="flex items-center gap-2 text-[#737373]" data-testid="current-meeting-done">
          <Clock className="w-4 h-4" /> Semana {currentWeek}: {formatDt(cur)} (já decorreu)
        </div>
      )}
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-[#E50000]" />
        <span className="text-[#A3A3A3]">Próxima reunião{nextWeek ? ` (semana ${nextWeek})` : ""}:</span>
        <strong data-testid="next-meeting-dt">{nextWeek ? formatDt(schedule[nextWeek]) : "não há mais reuniões nesta edição"}</strong>
      </div>
    </div>
  );
}

function AdminOverviewGrid({ currentWeek, rows }) {
  return (
    <div className="mt-5 pt-4 border-t border-[#222]" data-testid="admin-meetings-overview">
      <div className="kw-overline mb-3">Vista geral · Semana {currentWeek}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {rows.map((row) => (
          <div key={row.club} className="border border-[#222] p-3 flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-black uppercase tracking-tight">{row.club}</div>
              <div className="text-[10px] text-[#737373] uppercase tracking-widest">
                {row.weekday !== undefined && row.weekday !== null
                  ? `${WEEKDAYS[row.weekday]} · ${row.time || "—"}`
                  : "Sem horário definido"}
              </div>
            </div>
            <div className="text-right text-xs">
              {row.iso
                ? <strong className="text-[#E50000]" data-testid={`overview-${row.club}`}>{formatShort(row.iso)}</strong>
                : <span className="text-[#737373]">Por configurar</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function formatDt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", { weekday: "long", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function formatShort(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
